import { useState, useEffect, useRef } from "react";
import { fieldType } from "../../../../types/form";
import { DocumentIcon } from "@heroicons/react/24/solid";
import { FileIcon, DownloadIcon } from "../../../ui-icons/upload";
import Trash from "../../../ui-icons/buttons/Trash";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { selectDeleteStatus, selectGetStatus, selectUpdateStatus } from "src/modules/spItems/selectors";
import Loading from "src/components/Loading";
import moment from 'moment';
import { addItemRequest } from "src/modules/spItems/api";
import { selectCurrentUser } from "src/modules/users/selector";
import { addAnalysis } from "src/modules/acadia/constants";
import { siteUrlOrSiteApi } from "src/api/config/systemSwitch";
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { ActionSheet, ActionSheetButtonStyle } from '@capacitor/action-sheet';
import { documentByCardFilesLibName, siteUrl, strapiAdminUrl } from "src/api/config";
import { FlexCol } from "src/utils/flexLayouts";
import EyeIcon from "src/components/ui-icons/reports/EyeIcon";
import axios from "axios";
import { isPWA } from "src/utils/mobile";
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { resolveResourceUrl } from "src/utils/url.utils";
import { normalizeJpeg } from "src/pages/DashboardPage/mytasks/utils/utils";

const siteUrlProd = 'https://supply.napitkivmeste.ru'; // для корректного отображения превью в карточке документа

type Props = {
  dataField: fieldType;
  onChange: (data: any) => void;
  errors?: any;
  value: any;
  type: string;
  setIdRemoveElem: any;
  documentID?: any;
  documentCode?: any;
  listUploadFiles?: any;
  openRemoveFileCommentModal?: any;
  setOpenRemoveFileCommentModal?: any;
  setImagesDoc?: any
};

function UploadFiles(props: Props) {
  const {
    dataField,
    onChange,
    errors,
    value,
    type,
    setIdRemoveElem,
    documentCode,
    documentID,
    listUploadFiles,
    openRemoveFileCommentModal,
    setOpenRemoveFileCommentModal,
    setImagesDoc
  } = props;
  const [dragActive, setDragActive] = useState<boolean>(false);
  const inputRef = useRef<any>(null);
  const [files, setFiles] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [loadingFileName, setLoadingFileName] = useState("");
  const [progressUpload, setProgressUpload] = useState(0);
  const statusUpdateFile = useAppSelector((state: any) => selectUpdateStatus(state, listUploadFiles || documentByCardFilesLibName));
  const statusGetFile = useAppSelector((state: any) => selectGetStatus(state, listUploadFiles || documentByCardFilesLibName));
  const [idxRemoveFile, setIdxRemoveFile] = useState<any>(null);
  const currentUser = useAppSelector(selectCurrentUser);
  const [customLoading, setCustomLoading] = useState(false);

  useEffect(() => {
    if (value && Array.isArray(value) && value.length > 0) {
      let retFile: any = [];
      let files: any = [];
      files = value || [];
      if (files.length > 0) {
        files.forEach((file: any) => {
          if (file.ID) {
            retFile = [...retFile, {
              url: file.FileRef,
              name: file.FileLeafRef,
              id: file.ID,
            }]
          } else {
            retFile = [...retFile, file]
          }
        });
        setFiles(retFile);
      }
    }
  }, [type, value]);

  const handleChange = async (e: any) => {
    e.preventDefault();

    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    // Сразу забираем файлы из FileList
    const selectedFiles = Array.from(e.target.files) as File[];

    const addedFiles: File[] = [];

    setLoading(true);

    for (const file of selectedFiles) {
      if (file.size >= 52428800) {
        alert(
          `${file.name} - этот файл превышает допустимый размер в 50 MB`
        );
        continue;
      }

      setLoadingFileName(file.name);

      const fileReader = new FileReader();

      fileReader.readAsBinaryString(file);

      fileReader.onload = () => {
        console.log('FileReader loaded:', file.name);
      };

      fileReader.onprogress = (data) => {
        if (data.lengthComputable) {
          const progress = parseInt(
            String((data.loaded / data.total) * 100),
            10
          );

          setProgressUpload(progress);
        }
      };

      // Ждём нормализацию
      const normalizedFile = await normalizeJpeg(file);

      addedFiles.push(normalizedFile);
    }

    if (addedFiles.length > 0) {
      const updatedFiles = [...files, ...addedFiles];

      setFiles(updatedFiles);
      onChange(updatedFiles);
    }

    setLoadingFileName('');
    setLoading(false);

    onAllFilesLoaded();
  };

  const onAllFilesLoaded = () => {
    setLoading(false);
    setLoadingFileName("");
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      let loaded = 0;
      const addedFiles: File[] = [];
      for (let i = 0; i < e.dataTransfer.files.length; i++) {
        if (e.dataTransfer.files[i]['size'] >= 52428800) {
          alert(`${e.dataTransfer.files[i]['name']} - этот файл превышает допустимый размер`);
        } else {
          setLoading(true);
          setLoadingFileName(e.dataTransfer.files[i]["name"]);
          const total = e.dataTransfer.files.length;
          const fileReader = new FileReader();
          fileReader.readAsBinaryString(e.dataTransfer.files[i]);
          fileReader.onload = () => {
            // do something on FileReader onload
            loaded++;
            setLoadingFileName("");
            if (loaded == total) {
              onAllFilesLoaded();
            }
          };
          fileReader.onprogress = (data) => {
            if (data.lengthComputable) {
              const progress = parseInt(String((data.loaded / data.total) * 100), 10);
              setProgressUpload(progress);
            }
          };
          addedFiles.push(e.dataTransfer.files[i]);
        }
      }
      if (addedFiles.length > 0) {
        const updatedFiles = [...files, ...addedFiles];
        setFiles(updatedFiles);
        onChange(updatedFiles);
      }
    }
  };

  const handleDragLeave = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragEnter = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const removeFile = (e: any, fileName: any, idx: any, idFile: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!idFile) {
      const newArr = [...files];
      newArr.splice(idx, 1);
      setFiles(newArr);
      onChange(newArr);
    } else {
      if (type === 'add_comment') {
        localStorage.setItem('removeCommentFileInActionCard', JSON.stringify(idFile));
        setOpenRemoveFileCommentModal(!openRemoveFileCommentModal);
      } else if (type === 'audit_card_my_tasks') {
        localStorage.setItem('removeAuditFile', JSON.stringify(idFile));
        setOpenRemoveFileCommentModal(!openRemoveFileCommentModal);
      } else {
        setIdRemoveElem({ listName: listUploadFiles || documentByCardFilesLibName, ID: idFile });
      }
      setIdxRemoveFile(idx);
    }
  };

  useEffect(() => {
    if (statusUpdateFile === "SUCCESS" && idxRemoveFile !== null) {
      const newArr = [...files];
      newArr.splice(idxRemoveFile, 1);
      setFiles(newArr);
      onChange(newArr);
      setIdxRemoveFile(null);
    }
  }, [statusUpdateFile]);

  const handleSelectFile = async () => {
    if (isPWA()) {
      inputRef.current.value = "";
      inputRef.current.click();
      return;
    }

    try {
      const result = await ActionSheet.showActions({
        title: 'Добавить файл',
        options: [
          { title: 'Сделать фото' },
          { title: 'Выбрать из галереи' },
          { title: 'Выбрать файл' },
          {
            title: 'Отмена',
            style: ActionSheetButtonStyle.Cancel,
          },
        ],
      });

      // 📷 Камера
      if (result.index === 0) {
        const image = await Camera.getPhoto({
          quality: 90,
          resultType: CameraResultType.Uri,
          source: CameraSource.Camera,
        });

        await processImage(image.webPath);
      }

      // 🖼 Галерея
      if (result.index === 1) {
        const image = await Camera.getPhoto({
          quality: 90,
          resultType: CameraResultType.Uri,
          source: CameraSource.Photos,
        });

        await processImage(image.webPath);
      }

      // 📁 Файлы (старый input)
      if (result.index === 2) {
        inputRef.current.value = "";
        inputRef.current.click();
      }

    } catch (e) {
      console.log('User cancelled', e);
    }
  };

  const processImage = async (webPath?: string) => {
    if (!webPath) return;
    console.log('webPath', webPath)

    const response = await fetch(webPath);
    console.log('responseFile', response);
    const blob = await response.blob();
    console.log('blobFile', blob);

    const file = new File([blob], `photo_${Date.now()}.jpg`, {
      type: blob.type,
    });

    console.log(file);

    console.log({
      webPath,
      blobType: blob.type,
      blobSize: blob.size,
      fileType: file.type,
      fileSize: file.size,
  });

    // 👇 превращаем File в FileList и переиспользуем handleChange
    handleChange({
      target: {
        files: {
          0: file,
          length: 1,
          item: () => file,
        },
      },
      preventDefault: () => {},
    });

    console.log(file.type);
    console.log(file.size);
  };

  const downLoadHandle = (e: any, url: string, name: string, idFile: number) => {
    e.preventDefault();
    const downloadUrl = resolveResourceUrl(url, siteUrlOrSiteApi);
    if (type === "card") {
      addAnalysis(currentUser, documentID, documentCode, 'isActive', 'Download');
    }
    if (idFile && (type === 'card' || type === 'lists')) {
      fetch(downloadUrl).then((response) => response.blob()).then((blob) => {
        const printFile = new File([blob], name, {
          type: blob.type,
          lastModified: moment().unix(),
        });
        let reader = new FileReader();
        reader.readAsDataURL(printFile);
        reader.onload = function () {
          const src = reader.result;
          const linkDownLoad: HTMLAnchorElement = document.createElement("a");
          linkDownLoad.setAttribute("download", printFile.name);
          // @ts-ignore
          linkDownLoad.setAttribute("href", src);
          linkDownLoad.style.display = "none";
          document.body.appendChild(linkDownLoad);
          linkDownLoad.click();
          document.body.removeChild(linkDownLoad);
        };
      });
    } else {
      fetch(downloadUrl)
        .then((res) => {
          return res.blob();
        })
        .then((blob) => {
          const blobUrl = window.URL.createObjectURL(blob);

          const linkDownLoad: HTMLAnchorElement = document.createElement("a");
          linkDownLoad.href = blobUrl;
          linkDownLoad.download = name;
          document.body.appendChild(linkDownLoad);
          linkDownLoad.click();
          linkDownLoad.remove();
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch(() => {
          window.location.assign(downloadUrl);
        });
    }
  }

  return (
    <div className={`${dataField.width} mb-5`}>
      {(type === 'add_my_tasks_audit' || type === 'audit_card_my_tasks') &&
        <h3 className='text-[#637381] font-medium mb-2.5'>Вложения  · {files.length}</h3>
      }
      {dataField.multiple && files.length > 0 && (
        <>
          {(statusUpdateFile === "LOADING" && (
            <div className="flex flex-col items-center justify-center w-full">
              <Loading />
            </div>
          )) || (
              <div className="flex flex-col items-center justify-center w-full break-words">
                {files.map((file: any, idx: number) => (
                  <>
                    <a
                      onClick={(e) => {
                        if (file.id && (file.name.indexOf('.docx') !== -1 || file.name.indexOf('.xlsx') !== -1)) {
                          setCustomLoading(true);
                          e.preventDefault();
                          e.stopPropagation();
                          axios.post(`${siteUrl}/api/md-documents-files/preview_path`, {
                            id: file.id,
                            path: file.url.replace(siteUrl, strapiAdminUrl),
                          }, { withCredentials: true }).then(results => {
                            const { data } = results;
                            setImagesDoc(data.map((url: string) => `${siteUrl}${url}`));
                            setCustomLoading(false);
                          });
                        } else if (file.id && (file.name.indexOf('.jpg') !== -1 || file.name.indexOf('.jpeg') !== -1 || file.name.indexOf('.gif') !== -1 || file.name.indexOf('.png') !== -1)) {
                          e.preventDefault();
                          e.stopPropagation();
                          setImagesDoc([file.url]);
                        } else {
                          e.preventDefault();
                          e.stopPropagation();
                          downLoadHandle(
                            e,
                            file.url || URL.createObjectURL(file),
                            file.name,
                            file.id
                          );
                        }
                      }}
                      href="#"
                      key={idx}
                      className="box-border relative z-10 flex flex-row justify-between w-full gap-3 px-4 py-2 mb-5 rounded-lg cursor-pointer upload bg-sp-gray"
                    >
                      <div className="flex flex-col grow">
                        <div className="flex flex-row gap-x-2.5">
                          <div className="flex flex-col items-center justify-center">
                            <FileIcon />
                          </div>
                          <div className="flex flex-col">{file.name}</div>
                        </div>
                      </div>
                      <div className="relative z-10 flex flex-row">
                        {((file.id && (file.name.indexOf('.docx') !== -1 || file.name.indexOf('.xlsx') !== -1)) || (file.id && (file.name.indexOf('.jpg') !== -1 || file.name.indexOf('.jpeg') !== -1 || file.name.indexOf('.gif') !== -1 || file.name.indexOf('.png') !== -1))) &&
                          <span className="inline-block mr-[10px] mt-[3px]">
                            {customLoading &&
                              <FlexCol className="flex w-6 h-6">
                                <Loading />
                              </FlexCol> ||
                              <EyeIcon />
                            }
                          </span>
                        }
                        <span className="inline-block mr-[30px]">
                          <DownloadIcon />
                        </span>
                        {!dataField.disabled && (
                          <span
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeFile(e, file.name, idx, file.id);
                            }}
                            className="absolute right-0 z-20 inline-block"
                          >
                            <Trash />
                          </span>
                        )}
                      </div>
                    </a>
                  </>
                ))}
              </div>
            )}
        </>
      )}
      {(files.length === 0 || (dataField.multiple && !dataField.disabled)) && (
        <div
          onDragEnter={handleDragEnter}
          onDrop={handleDrop}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          className="relative flex justify-center px-6 py-10 mt-2 border border-dashed rounded-lg border-gray-900/25"
        >
          {!loading && (
            <div className="text-center">
              {(statusGetFile === "LOADING" && <Loading />) || (
                <>
                  <DocumentIcon className="w-12 h-12 mx-auto text-gray-300" aria-hidden="true" />
                  <div className="my-3.5 flex flex-col-reverse text-sm leading-6 text-gray-600">
                    <p className="hidden sm:block pl-1 text-base">{dataField.title}</p>
                    <div className="text-base relative outline-none cursor-pointer rounded-md bg-white font-semibold text-[#012169]">
                      &nbsp;
                      <span onClick={handleSelectFile}>{dataField.titleFile}</span>
                      <input
                        ref={inputRef}
                        multiple={dataField.multiple || false}
                        onChange={handleChange}
                        accept={(type === 'card' || type === 'add_card') && ".xlsx, .docx, .pdf, .pptx, .txt" || undefined}
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        className="sr-only"
                      />
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">{dataField.placeholder}</p>
                </>
              )}
            </div>
          )}
          {dataField.require && (
            <div className="text-[#BA0C2F] absolute z-20 right-0 top-[-45px] flex flex-row justify-between">
              {Object.keys(errors).length > 0 && errors[dataField.key] && (
                <span className="text-[#BA0C2F]">Заполните поле&nbsp;</span>
              )}
              <span className="text-[#BA0C2F]">*</span>
            </div>
          )}
          {loading && (
            <div className="flex flex-col items-center justify-center w-full">
              <div className="mb-4 h-6 max-w-md w-full bg-[#DFE4EA] rounded-full">
                <div className="h-6 bg-[#012169] rounded-full" style={{ width: `${progressUpload}%` }} />
              </div>
              <div className="text-lg mb-4 text-[#637381] font-medium">Загрузка файла...</div>
              <div className="text-sm text-[#637381]">{loadingFileName}</div>
            </div>
          )}
        </div>
      )}
      {!dataField.multiple && files.length > 0 && (
        <div className="flex flex-col items-center justify-center w-full">
          {files.map((file: any, idx: number) => (
              <a
                onClick={async (e) => {
                  if (type === 'card' && file.id && (file.name.indexOf('.docx') !== -1 || file.name.indexOf('.xlsx') !== -1)) {
                    setCustomLoading(true);
                    e.preventDefault();
                    e.stopPropagation();
                    let token: string | null = null;

                    if (!isPWA()) {
                      const result = await SecureStoragePlugin.get({ key: 'accessToken' });
                      token = result.value;
                    }
                    const headers = token
                      ? {
                          headers: {
                            Authorization: `Bearer ${token}`,
                          },
                        }
                      : {};
                    axios(`${siteUrl}/api/md-documents-files/preview/${file.id}`, headers).then((result) => {
                      setImagesDoc(result['data']['DocumentPreviewPaths'].map((url: string) => `${siteUrlProd}${url}`));
                      setCustomLoading(false);
                    });
                  } else if (file.id && (file.name.indexOf('.jpg') !== -1 || file.name.indexOf('.jpeg') !== -1 || file.name.indexOf('.gif') !== -1 || file.name.indexOf('.png') !== -1)) {
                    e.preventDefault();
                    e.stopPropagation();
                    setImagesDoc([file.url]);
                  } else {
                    e.preventDefault();
                    e.stopPropagation();
                    downLoadHandle(
                      e,
                      file.url || URL.createObjectURL(file),
                      file.name,
                      file.id
                    );
                  }
                }}
                href="#"
                key={file.id || file.name || idx}
                className="box-border relative z-10 flex flex-row justify-between w-full gap-3 px-4 py-2 mb-5 rounded-lg cursor-pointer upload bg-sp-gray"
              >
                <div className="flex flex-col grow">
                  <div className="flex flex-row gap-x-2.5">
                    <div className="flex flex-col items-center justify-center">
                      <FileIcon />
                    </div>
                    <div className="flex flex-col text-[14px] tablet:text-[16px] break-words tablet:break-normal">{file.name}</div>
                  </div>
                </div>
                <div className="relative z-10 flex flex-row">
                  {((type === 'card' && file.id && (file.name.indexOf('.docx') !== -1 || file.name.indexOf('.xlsx') !== -1)) || (file.id && (file.name.indexOf('.jpg') !== -1 || file.name.indexOf('.jpeg') !== -1 || file.name.indexOf('.gif') !== -1 || file.name.indexOf('.png') !== -1))) &&
                    <span className="inline-block mr-[10px] mt-[3px]">
                      {customLoading &&
                        <FlexCol className="flex w-6 h-6">
                          <Loading />
                        </FlexCol> ||
                        <EyeIcon />
                      }
                    </span>
                  }
                  <span onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    downLoadHandle(
                      e,
                      file.url || URL.createObjectURL(file),
                      file.name,
                      file.id
                    );
                  }} className="inline-block mr-[30px]">
                    <DownloadIcon />
                  </span>
                  {!dataField.disabled && (
                    <>
                      {(statusUpdateFile === "LOADING" && <Loading />) || (
                        <span
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeFile(e, file.name, idx, file.id)
                          }}
                          className="absolute right-0 z-20 hidden tablet:inline-block"
                        >
                          <Trash />
                        </span>
                      )}
                    </>
                  )}
                </div>
              </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default UploadFiles;
